import { ColDef, ValueGetterParams } from "ag-grid-community";
import { capitalize } from "@mui/material";
import { CustomCellRendererProps } from "ag-grid-react";
import { BudgetsPeriodCell } from "./cells/BudgetsPeriodCell";
import { BudgetsProgressCell } from "./cells/BudgetsProgressCell";
import { BudgetListActionsCell } from "./cells/BudgetListActionsCell";
import { BudgetsNameCell } from "./cells/BudgetsNameCell";
import { BudgetsScopeCell } from "./cells/BudgetsScopeCell";
import { BudgetTypeCell } from "./cells/BudgetTypeCell";
import { BudgetAmountCell } from "./cells/BudgetAmountCell";
import { budgetsThresholdSortingFn } from "../utils/helpers/sorting-functions/budgetsThresholdSortingFn";
import { agGridEmptyColumnToFitEmptySpace } from "../../../../../common/ag-grid/utils/constants/agGridEmptyColumnToFitEmptySpace";
import { keyInsensitiveComparatorFn } from "../../../../../common/ag-grid/utils/helpers/sortingFunctions/keyInsensitiveComparatorFn";
import { isNotStartedYet } from "../utils/helpers/isNotStartedYet";
import { Budget } from "@/services/cloudchipr.api";
import { budgetAmountCellValueGetter } from "@/components/pages/budgets-and-alerts/components/grids/budgets/utils/helpers/value-getters/budgetAmountCellValueGetter";
import { budgetMethodCellValueGetter } from "@/components/pages/budgets-and-alerts/components/grids/budgets/utils/helpers/value-getters/budgetMethodCellValueGetter";
import { money } from "@/utils/numeral/money";
import { createdByColumnDef } from "@/components/pages/common/ag-grid/components/columns/createdByColumnDef";
import { modifiedByColumnDef } from "@/components/pages/common/ag-grid/components/columns/modifiedByColumnDef";
import { BudgetWithIntegrations } from "@/store/budgets/utils/types/common";
import { notificationMethodColumnDef } from "@/components/pages/common/ag-grid/components/columns/notificationMethodColumnDef";
import { budgetScopeFilterValueGetter } from "@/components/pages/budgets-and-alerts/components/grids/budgets/utils/helpers/value-getters/budgetScopeFilterValueGetter";
import { budgetScopeValueGetter } from "@/components/pages/budgets-and-alerts/components/grids/budgets/utils/helpers/value-getters/budgetScopeValueGetter";
import { budgetNameValueGetter } from "@/components/pages/budgets-and-alerts/components/grids/budgets/utils/helpers/value-getters/budgetNameValueGetter";
import { budgetNameFilterValueGetter } from "@/components/pages/budgets-and-alerts/components/grids/budgets/utils/helpers/value-getters/budgetNameFilterValueGetter";
import { TypographyWithCopy } from "@/components/common/TypographyWithCopy";
import { agGridActionColumnDefaultColumnDef } from "@/components/pages/common/ag-grid/utils/constants/agGridActionColumnDefaultColumnDef";

export const budgetsGridColumns: ColDef<BudgetWithIntegrations>[] = [
  {
    minWidth: 200,
    width: 250,
    maxWidth: 350,
    field: "name",
    headerName: "Budget Name",
    pinned: "left",
    suppressColumnsToolPanel: true,
    cellRenderer: BudgetsNameCell,
    valueGetter: budgetNameValueGetter,
    comparator: keyInsensitiveComparatorFn,
    filterValueGetter: budgetNameFilterValueGetter,
  },
  {
    minWidth: 200,
    width: 250,
    maxWidth: 350,
    field: "view.name",
    headerName: "Billing Scope",
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<BudgetWithIntegrations>) => (
      <BudgetsScopeCell budget={data} />
    ),
    comparator: keyInsensitiveComparatorFn,
    valueGetter: budgetScopeValueGetter,
    filterValueGetter: budgetScopeFilterValueGetter,
  },
  {
    minWidth: 150,
    width: 200,
    maxWidth: 300,
    colId: "budgetAmount",
    headerName: "Budget",
    cellRenderer: BudgetAmountCell,
    valueGetter: ({ data }) => budgetAmountCellValueGetter(data),
  },
  {
    minWidth: 150,
    width: 200,
    maxWidth: 300,
    field: "used",
    headerName: "Used",
    cellRenderer: ({ value }: CustomCellRendererProps<Budget, number>) => (
      <TypographyWithCopy value={money(value)} variant="body2" />
    ),
  },
  {
    minWidth: 150,
    width: 200,
    maxWidth: 300,
    field: "forecast",
    headerName: "Forecast",
    cellRenderer: ({ data }: CustomCellRendererProps<Budget, number>) => (
      <TypographyWithCopy value={money(data?.forecast)} variant="body2" />
    ),
  },
  {
    minWidth: 150,
    width: 230,
    maxWidth: 300,
    colId: "progress",
    headerName: "Used vs Budgeted",
    valueGetter: ({ data }: ValueGetterParams<BudgetWithIntegrations>) => {
      if (isNotStartedYet(data?.start_date)) {
        return "Not Started";
      }
      const amount = budgetAmountCellValueGetter(data);
      return parseFloat((((data?.used ?? 0) / (amount ?? 1)) * 100).toFixed(2));
    },
    cellRenderer: BudgetsProgressCell,
  },
  {
    minWidth: 200,
    width: 220,
    maxWidth: 300,
    colId: "BudgetMethodCell",
    headerName: "Budgeting Method",
    valueGetter: ({ data }) => budgetMethodCellValueGetter(data),
    filterValueGetter: ({ data }) => capitalize(data?.type ?? ""),
    cellRenderer: BudgetTypeCell,
  },
  {
    minWidth: 150,
    width: 200,
    maxWidth: 300,
    field: "period",
    headerName: "Period",
    valueGetter: ({ data }) => capitalize(data?.period ?? ""),
    cellRenderer: BudgetsPeriodCell,
  },
  {
    minWidth: 150,
    width: 200,
    maxWidth: 300,
    field: "thresholds",
    headerName: "Threshold",
    valueGetter: ({ data }) =>
      data?.thresholds.map(({ percent }) => `${percent}%`).join(", "),
    comparator: budgetsThresholdSortingFn,
    cellRenderer: ({
      value,
    }: CustomCellRendererProps<Budget, string | undefined>) => (
      <TypographyWithCopy value={value ?? undefined} variant="body2" />
    ),
  },
  createdByColumnDef,
  modifiedByColumnDef,
  notificationMethodColumnDef,

  agGridEmptyColumnToFitEmptySpace,
  {
    ...agGridActionColumnDefaultColumnDef,
    width: 70,
    minWidth: 70,
    colId: "actions",
    cellDataType: false,
    cellRenderer: BudgetListActionsCell,
  },
];
